<template>
  <ImageTextTeasersBlock
    :teasers="mappedTeasers"
    :cols="mappedCols"
    :cols-centered="item.fields.teaserCenteredInRow ?? false"
    :content-centered="item.fields.teaserCentered ?? false"
    :is-image-stretched="item.fields.stretchedImage ?? false"
    :is-rounded="item.fields.roundedImages ?? true"
    :teaser-cols="mappedTeasersCols"
    v-bind="
      $ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'teasers'
      })
    "
  />
</template>

<script setup lang="ts">
import { ImageTextTeasersBlock } from '@hypercodestudio/basler-components';
import type { ImageTextTeaserInterface } from '@hypercodestudio/basler-components';

import type { ITextImageTeaserList } from '~~/lib/ContentfulService';
import { generateImageObject } from '~/utils/generateImageObject';
import { mapColsWidth } from '~/utils/mapColsWidth';
import { isEntryResolveError } from '~/utils/guards/isEntryResolveError';
import { buildTextImageTeaserImageOptions } from '~/utils/imagePresetRegistry';
import { hasHeadlineTextImageTeaserContent } from '~/utils/hasRenderableTeaserContent';
import { isTextTeaserCardLinkVisibleInLocale } from '~/utils/contentful/isTextTeaserCardLinkVisibleInLocale';
const { $ContentfulLivePreview, $activeLocale } = useNuxtApp();

interface Props {
  item: ITextImageTeaserList;
}

const props = defineProps<Props>();

const imageSize = props.item.fields.imageSize ?? '';

const mappedTeasers = computed(
  () =>
    props.item.fields.teasers
      ?.filter((entry) => !isEntryResolveError(entry))
      ?.filter((entry) =>
        isTextTeaserCardLinkVisibleInLocale(
          entry.fields.link?.fields.internalPage,
          entry.sys?.locale ?? $activeLocale.value.contentfulCode
        )
      )
      ?.filter((entry) => hasHeadlineTextImageTeaserContent(entry.fields))
      ?.map<ImageTextTeaserInterface>((teaser) => ({
        headline: teaser.fields.headline,
        text: teaser.fields.text ?? '',
        image:
          teaser.fields.image &&
          generateImageObject(teaser.fields.image, {
            ...buildTextImageTeaserImageOptions(imageSize)
          })?.content,
        buttonText: teaser.fields.link?.fields?.linkText,
        buttonLink: useBuildLinkInterface(teaser.fields.link),
        buttonStyle: teaser.fields.link?.fields?.linkStyle
      })) ?? []
);

const mappedTeasersCols = computed(() => {
  switch (mappedTeasers.value.length) {
    case 1:
      return [12, 12, 12];
    case 2:
      return [6, 12, 12];
    case 3:
      return [4, 6, 12];
    case 4:
      return [3, 6, 12];
    default:
      return [6, 6, 6];
  }
});

const mappedCols = computed(
  () => mapColsWidth(props.item.fields.teaserWidth) ?? 10
);
</script>
